/**
 * Links: detection in message text, address safety checks for the server-side fetcher, and a
 * small HTML-to-text pass. No I/O here; the fetcher itself lives in apps/api (lib/safe-fetch.ts).
 */

const URL_RE = /\bhttps?:\/\/[^\s<>"'`{}|\\^[\]]+/giu;
const TRAILING_PUNCT = /[.,;:!?)\]}»”’…]+$/u;

/** Distinct http(s) URLs in reading order, trailing punctuation stripped, at most `max`. */
export function extractUrls(text: string, max = 3): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(URL_RE)) {
    const raw = m[0].replace(TRAILING_PUNCT, '');
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      continue;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') continue;
    url.hash = '';
    const key = url.toString();
    if (!out.includes(key)) out.push(key);
    if (out.length >= max) break;
  }
  return out;
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./iu, '');
  } catch {
    return url;
  }
}

/** Parse a dotted IPv4 address into its 32-bit value, or undefined. */
function ipv4(ip: string): number | undefined {
  const parts = ip.split('.');
  if (parts.length !== 4) return undefined;
  let n = 0;
  for (const p of parts) {
    if (!/^\d{1,3}$/u.test(p)) return undefined;
    const v = Number(p);
    if (v > 255) return undefined;
    n = n * 256 + v;
  }
  return n;
}

const inRange = (n: number, base: string, bits: number) => {
  const b = ipv4(base);
  if (b === undefined) return false;
  const mask = bits === 0 ? 0 : (~0 << (32 - bits)) >>> 0;
  return (n & mask) >>> 0 === (b & mask) >>> 0;
};

/**
 * True for addresses the link fetcher must never contact: loopback, private (RFC 1918),
 * link-local (169.254/16, fe80::/10), CGNAT (100.64/10), multicast, unspecified, ULA
 * (fc00::/7), the "this network" block, and IPv4-mapped forms of any of those.
 */
export function isPrivateAddress(ip: string): boolean {
  const v = ip.trim().toLowerCase();
  if (!v) return true;
  if (v.startsWith('[') && v.endsWith(']')) return isPrivateAddress(v.slice(1, -1));
  if (v.startsWith('::ffff:')) return isPrivateAddress(v.slice(7));
  const n = ipv4(v);
  if (n !== undefined) {
    return (
      inRange(n, '0.0.0.0', 8) ||
      inRange(n, '10.0.0.0', 8) ||
      inRange(n, '100.64.0.0', 10) ||
      inRange(n, '127.0.0.0', 8) ||
      inRange(n, '169.254.0.0', 16) ||
      inRange(n, '172.16.0.0', 12) ||
      inRange(n, '192.0.0.0', 24) ||
      inRange(n, '192.168.0.0', 16) ||
      inRange(n, '198.18.0.0', 15) ||
      inRange(n, '224.0.0.0', 3)
    );
  }
  if (v.includes(':')) {
    if (v === '::' || v === '::1') return true;
    if (/^f[cd][0-9a-f]{2}:/u.test(v)) return true; // fc00::/7
    if (/^fe[89ab][0-9a-f]:/u.test(v)) return true; // fe80::/10
    if (/^ff[0-9a-f]{2}:/u.test(v)) return true; // multicast
    if (v.startsWith('64:ff9b:')) return true; // NAT64 well-known prefix
    return false;
  }
  return true; // not an IP literal at all: the caller resolves names first
}

/** Hostnames that must never be fetched even before resolution. */
export function isBlockedHostname(host: string): boolean {
  const h = host.toLowerCase().replace(/\.$/u, '');
  return (
    h === 'localhost' ||
    h.endsWith('.localhost') ||
    h.endsWith('.local') ||
    h.endsWith('.internal') ||
    h === 'metadata.google.internal' ||
    h === 'instance-data'
  );
}

export interface ReadableText {
  title: string;
  description: string;
  text: string;
}

const decodeEntities = (s: string) =>
  s
    .replace(/&nbsp;/giu, ' ')
    .replace(/&amp;/giu, '&')
    .replace(/&lt;/giu, '<')
    .replace(/&gt;/giu, '>')
    .replace(/&quot;/giu, '"')
    .replace(/&#39;|&apos;/giu, "'")
    .replace(/&#(\d+);/gu, (_, d: string) => String.fromCodePoint(Number(d)))
    .replace(/&#x([0-9a-f]+);/giu, (_, h: string) => String.fromCodePoint(parseInt(h, 16)));

const attr = (tag: string, name: string) =>
  new RegExp(`${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'iu').exec(tag)?.[2] ??
  new RegExp(`${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'iu').exec(tag)?.[3];

/**
 * Readable text from HTML by simple heuristics: <title>, meta description / og:description,
 * then the main content (<article>, <main>, or the body) with scripts, styles, nav, header,
 * footer and aside removed, block boundaries turned into line breaks. Capped at `maxChars`.
 */
export function htmlToText(html: string, maxChars = 20_000): ReadableText {
  const title = decodeEntities(
    /<title[^>]*>([\s\S]*?)<\/title>/iu.exec(html)?.[1]?.replace(/\s+/gu, ' ').trim() ?? '',
  );
  let description = '';
  for (const m of html.matchAll(/<meta\b[^>]*>/giu)) {
    const tag = m[0];
    const name = (attr(tag, 'name') ?? attr(tag, 'property') ?? '').toLowerCase();
    if (name === 'description' || name === 'og:description') {
      description = decodeEntities((attr(tag, 'content') ?? '').replace(/\s+/gu, ' ').trim());
      if (description) break;
    }
  }
  const main =
    /<article\b[^>]*>([\s\S]*?)<\/article>/iu.exec(html)?.[1] ??
    /<main\b[^>]*>([\s\S]*?)<\/main>/iu.exec(html)?.[1] ??
    /<body\b[^>]*>([\s\S]*?)<\/body>/iu.exec(html)?.[1] ??
    html;
  const text = decodeEntities(
    main
      .replace(
        /<(script|style|noscript|svg|template|nav|header|footer|aside|form)\b[\s\S]*?<\/\1>/giu,
        ' ',
      )
      .replace(/<!--[\s\S]*?-->/gu, ' ')
      .replace(/<br\s*\/?>/giu, '\n')
      .replace(/<\/(p|div|li|h[1-6]|tr|section|article|blockquote|pre|dd|dt)>/giu, '\n')
      .replace(/<[^>]+>/gu, ' '),
  )
    .replace(/[ \t\u00a0]+/gu, ' ')
    .replace(/ *\n */gu, '\n')
    .replace(/\n{3,}/gu, '\n\n')
    .trim()
    .slice(0, maxChars);
  return { title, description, text };
}
